import { capitalizeDrugName, concatenateStrengthsConcise } from '@/src/utils'
import { Box, Button, Spinner, Text } from 'grommet'
import moment from 'moment'
import { ReactNode, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { DeleteIcon, DisabledIcon, PauseIcon, PencilIcon } from '../../../../../assets/svgs'
import { Badge } from '../../../../../components/common'
import InformationList from '../../../../../components/common/InformationList'
import { SideDrawer } from '../../../../../components/Modals'
import { useMedAdministration } from '../../../../../hooks/useMedAdminist'
import { useMedication } from '../../../../../hooks/useMedication'
import { useAppDispatch } from '../../../../../store/hooks'
import { showDeleteMedications, showEditMedications } from '../../../../../store/slicers/ModalSlicer'
import { MedicationDataMock, statusType } from '../../../../../types/medication.type'
import { guardEncounterWrite } from '../../../../../utils/encounterWriteGuard'
import { showToast } from '../../../../../utils/toast'
import { useEncounterReadOnly } from '../../EncounterReadOnlyContext'

type Props = {
  onClose: () => void
  defaultData?: MedicationDataMock
}

const detailOptions = ['order details', 'patient Details']

export const ReconciliationDetails = ({ onClose, defaultData }: Props) => {
  const [tab, setTab] = useState('order details')
  const dispatch = useAppDispatch()
  const { encounterId: encounter_id } = useParams()
  const { isEncounterReadOnly } = useEncounterReadOnly()

  // Using useMedication hook for medication details
  const { medication, includedMed, singleMedIsLoading, patientMedication, updateMedicationStatus } = useMedication({
    id: defaultData?.id,
    patient: defaultData?.relationships.patient.data?.id
  })

  // Using useMedAdministration for MAR data
  const { medications: adminMedication } = useMedAdministration({
    encounter_id,
    id: defaultData?.id
  })

  const onOptionClick = async (id: string, status: keyof typeof statusType) => {
    if (!guardEncounterWrite()) return
    try {
      await updateMedicationStatus({ id, payload: { status } })
      showToast('Status updated successfully')
      onClose()
    } catch (error: any) {
      showToast(error?.response?.data?.errors?.[0]?.detail || 'An error occurred')
    }
  }

  const medicationInfos = useMemo(() => {
    if (!medication) return {}

    const baseInfo = {
      comment: medication.attributes.comment || '-',
      dosage: medication.attributes.dosage?.toLowerCase(),
      'end date': moment(medication.attributes.end_at).format('DD/MM/YYYY'),
      form: medication.attributes.form,
      frequency: includedMed?.find(
        ({ type, id }) =>
          type === 'medication_frequency' && id === medication.relationships.medication_frequency.data?.id
      )?.attributes.code,
      medication: includedMed?.find(
        ({ type, id }) => type === 'product' && id === medication.relationships.product.data?.id
      )?.attributes.name,
      'medication type': medication.attributes.medication_type.toUpperCase(),
      quantity: medication.attributes.quantity,
      route: medication.attributes.route,
      'start date': moment(medication.attributes.start_at).format('DD/MM/YYYY')
    }

    // Check if medication has been administered and modified
    const isAdministered = medication.attributes.administered_at !== null
    const isModified = medication.attributes.modified_at !== null

    if (isAdministered && isModified && adminMedication && adminMedication?.length >= 2) {
      const [newPrescription, previousPrescription] = adminMedication

      return {
        'New Prescription': {
          Date: moment(newPrescription.attributes.scheduled_for).format('DD/MM/YYYY HH:mm'),
          'Modified at': moment(medication.attributes.modified_at).format('DD/MM/YYYY HH:mm'),
          'Modified by': `${medication.relationships.staff.data.attributes?.first_name} ${medication.relationships.staff.data.attributes?.last_name}`,
          comment: medication.attributes.comment || '-',
          dosage: newPrescription.attributes.medication_details.dosage?.toLowerCase(),
          'end date': moment(medication.attributes.end_at).format('DD/MM/YYYY'),
          form: newPrescription.attributes.medication_details.form,
          frequency: newPrescription.attributes.medication_details.medication_frequency_code,
          medication: capitalizeDrugName(newPrescription.attributes.medication_details.drug_name),
          'medication type': newPrescription.attributes.medication_details.medication_type.toUpperCase(),
          quantity: newPrescription.attributes.medication_details.dose_quantity,
          route: newPrescription.attributes.medication_details.route,
          'start date': moment(medication.attributes.start_at).format('DD/MM/YYYY')
        },
        'Previous Prescription': {
          Date: moment(previousPrescription.attributes.scheduled_for).format('DD/MM/YYYY HH:mm'),
          'Ordered by': `${medication.relationships.staff.data.attributes?.first_name} ${medication.relationships.staff.data.attributes?.last_name}`,
          comment: medication.attributes.comment || '-',
          dosage: previousPrescription.attributes.medication_details.dosage?.toLowerCase(),
          'end date': moment(medication.attributes.end_at).format('DD/MM/YYYY'),
          form: previousPrescription.attributes.medication_details.form,
          frequency: previousPrescription.attributes.medication_details.medication_frequency_code,
          medication: capitalizeDrugName(previousPrescription.attributes.medication_details.drug_name),
          'medication type': previousPrescription.attributes.medication_details.medication_type.toUpperCase(),
          quantity: previousPrescription.attributes.medication_details.dose_quantity,
          route: previousPrescription.attributes.medication_details.route,
          'start date': moment(medication.attributes.start_at).format('DD/MM/YYYY')
        }
      }
    }

    return baseInfo
  }, [medication, includedMed, adminMedication])

  const patientInfo = useMemo(() => {
    if (medication) {
      const patient = includedMed?.find(
        ({ type, id }) => type === 'patient' && id === medication.relationships.patient.data?.id
      )
      return {
        DOB: patient?.attributes.date_of_birth,
        Name: `${patient?.attributes.first_name} ${patient?.attributes.last_name}`
      }
    }
    return {}
  }, [medication, includedMed])

  const encounterInfo = useMemo(() => {
    if (medication) {
      const encounter = includedMed?.find(
        ({ type, id }) => type === 'encounter' && id === medication.relationships.encounter?.data?.id
      )
      return {
        Problem: encounter?.attributes.name,
        'Start date': encounter?.attributes.start_date
          ? moment(encounter?.attributes.start_date).format('DD/MM/YYYY')
          : '---'
      }
    }
    return {}
  }, [medication, includedMed])

  const allergiesInfo = useMemo(() => {
    if (medication) {
      const allergies = includedMed?.filter(
        ({ type, id }) =>
          type === 'allergy' && medication.relationships?.allergies?.data.some(({ id: filterId }) => id === filterId)
      )

      return (
        allergies
          ?.map(({ attributes }) => attributes)
          .reduce<Record<string, string>>((acc, data, index) => {
            acc[`Drug name ${index + 1}`] = data.title || '---'
            return acc
          }, {}) || {}
      )
    }
    return {}
  }, [medication, includedMed])

  const onGoingMed: Record<string, string[]> = useMemo(() => {
    if (patientMedication) {
      return patientMedication.data.reduce<Record<string, string[]>>((acc, data, index) => {
        const product = patientMedication.included?.find(
          ({ type, id }) => type === 'product' && id === data.relationships.product.data?.id
        )?.attributes.name

        const medicationFreq = patientMedication?.included.find(
          ({ type, id }) => type === 'medication_frequency' && id === data.relationships.medication_frequency.data?.id
        )?.attributes.code

        const form = data?.attributes?.form?.trim()
        const strength = data?.attributes?.strength || concatenateStrengthsConcise(data?.attributes.api_doses)
        const formLabel = form || strength ? `Form: ${form || '-'}, Strength: ${strength || '-'}, ` : ''
        acc[`medication${index + 1}`] = [
          `${product || '---'}`,
          `Qty: ${data?.attributes?.quantity || '-'}, Dosage: ${data?.attributes?.dose_quantity ? data?.attributes?.dose_quantity + ' ' + data?.attributes?.dose_unit : data?.attributes?.strength || concatenateStrengthsConcise(data?.attributes.api_doses)}`,
          `${formLabel}Frequency: ${medicationFreq || '---'} ${data.attributes.medication_type.toUpperCase()}`
        ]
        return acc
      }, {})
    }
    return {}
  }, [patientMedication])

  const header = (
    <Box gap={'20px'} margin={{ bottom: '40px', vertical: '16px' }}>
      <Box align="center" direction="row" gap={'8px'}>
        <Text size="24px" weight={600}>
          {
            includedMed?.find(({ type, id }) => type === 'product' && id === medication?.relationships.product.data?.id)
              ?.attributes.name
          }
        </Text>
        <Badge label={medication?.meta.status} status={medication?.meta.status} />
      </Box>
      {!isEncounterReadOnly ? (
        <Box direction="row" margin={{ top: '.4em' }}>
          <Box direction="row">
            <Button
              className="!border !rounded-[10px] !border-[#E6E6E6] hover:!bg-[#E6E6E6] "
              color="#fff"
              icon={<PencilIcon />}
              label={'Modify'}
              margin={{ right: 'small' }}
              onClick={() => {
                if (defaultData?.id) {
                  dispatch(showEditMedications({ defaultData, id: defaultData?.id }))
                }
              }}
              pad={{ horizontal: '10px' }}
              secondary
              style={{ color: 'black', fontSize: '14px', fontWeight: 'normal' }}
              type="button"
            />
            <Button
              className="!border !rounded-[10px] !border-[#E6E6E6] hover:!bg-[#E6E6E6] "
              color="#fff"
              icon={<PauseIcon />}
              label="Put on Hold"
              margin={{ right: 'small' }}
              onClick={() => onOptionClick(defaultData?.id || '', 'onhold')}
              pad={{ horizontal: '10px' }}
              secondary
              style={{ color: 'black', fontSize: '14px', fontWeight: 'normal' }}
              type="button"
            />
            <Button
              className="!border !rounded-[10px] !border-[#E6E6E6] hover:!bg-[#E6E6E6] "
              color="#fff"
              icon={<DisabledIcon />}
              label="Discontinue"
              margin={{ right: 'small' }}
              onClick={() => onOptionClick(defaultData?.id || '', 'discontinued')}
              pad={{ horizontal: '10px' }}
              secondary
              style={{ color: 'black', fontSize: '14px', fontWeight: 'normal' }}
              type="button"
            />
            <Button
              className="!border !rounded-[10px] !border-[#E6E6E6] hover:!bg-[#E6E6E6] "
              color="#fff"
              icon={<DeleteIcon alt />}
              label="Delete"
              onClick={() => {
                if (defaultData?.id) {
                  dispatch(showDeleteMedications({ id: defaultData?.id }))
                }
              }}
              pad={{ horizontal: '10px' }}
              secondary
              style={{ color: '#E34949', fontSize: '14px', fontWeight: 'normal' }}
              type="button"
            />
          </Box>
        </Box>
      ) : null}
    </Box>
  )

  return (
    <SideDrawer
      header={header}
      onClose={onClose}
      options={detailOptions}
      setTab={setTab}
      style={{ padding: '0 40px' }}
      title="Order Details"
    >
      <Box flex style={{ overflowX: 'hidden', overflowY: 'auto', position: 'relative' }}>
        <div
          className={`step-slide ${tab === 'order details' ? 'active' : ''} px-5`}
          style={{
            position: 'absolute',
            transform: `translateX(${tab === 'order details' ? '0' : '-100%'})`,
            transition: 'all 0.3s ease-in-out',
            width: '100%'
          }}
        >
          {singleMedIsLoading && (
            <Box align="center" alignSelf="center" direction="row" gap="small" justify="center" pad="large">
              <Spinner height={'40px'} size="5px" width={'40px'} />
              <Text color="white">Loading...</Text>
            </Box>
          )}
          {medication && (
            <>
              {typeof medicationInfos === 'object' && 'New Prescription' in medicationInfos ? (
                <>
                  <InformationList
                    data={medicationInfos['New Prescription'] as { [key: string]: string[] | ReactNode }}
                    title="New Prescription"
                  />
                  <InformationList
                    data={medicationInfos['Previous Prescription'] as { [key: string]: string[] | ReactNode }}
                    title="Previous Prescription"
                  />
                </>
              ) : (
                <InformationList data={medicationInfos as { [key: string]: string[] | ReactNode }} />
              )}
            </>
          )}
        </div>
        <div
          className={`step-slide ${tab === 'patient Details' ? 'active' : ''} px-5`}
          style={{
            bottom: '0px',
            position: 'absolute',
            top: '0px',
            transform: `translateX(${tab === 'patient Details' ? '0' : '100%'})`,
            transition: 'all 0.3s ease-in-out',
            width: '100%'
          }}
        >
          {medication && <InformationList data={patientInfo} title="Patient" />}
          {medication && <InformationList data={encounterInfo} title="Encounter Problem" />}
          {medication && <InformationList data={allergiesInfo} title="Allergies" />}
          {medication && <InformationList data={onGoingMed} title="Ongoing Medications" />}
        </div>
      </Box>
    </SideDrawer>
  )
}
